import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * Updates the recently viewed products in localStorage.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products in session storage */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {number} The maximum number of products to store. One extra slot accounts for the
   * current product, which callers typically exclude from display, so up to 4 *other* products remain. */
  static #MAX_PRODUCTS = 5;

  /**
   * Adds a product to the recently viewed products list.
   * @param {string} productId - The ID of the product to add.
   */
  static addProduct(productId) {
    let viewedProducts = this.getProducts();

    viewedProducts = viewedProducts.filter((/** @type {string} */ id) => id !== productId);
    viewedProducts.unshift(productId);
    viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);

    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));
  }

  static clearProducts() {
    localStorage.removeItem(this.#STORAGE_KEY);
  }

  /**
   * Retrieves the list of recently viewed products from session storage.
   * @returns {string[]} The list of viewed products.
   */
  static getProducts() {
    return JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
  }
}

/**
 * Renders a grid of the shopper's recently viewed products, excluding the current product.
 * Records the current product as viewed on connect.
 */
class RecentlyViewedProducts extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { sectionId, searchUrl, currentProductId, maxProducts } = this.dataset;
    if (!sectionId || !searchUrl) return;

    if (currentProductId) {
      RecentlyViewed.addProduct(currentProductId);
    }

    const limit = Number(maxProducts) || 4;
    const ids = RecentlyViewed.getProducts()
      .filter((id) => id !== currentProductId)
      .slice(0, limit);

    if (!ids.length) {
      this.classList.add('hidden');
      return;
    }

    const url = new URL(searchUrl, location.origin);
    url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('type', 'product');

    sectionRenderer
      .renderSection(sectionId, { url, mode: 'full' })
      .then((html) => {
        if (!html || !html.trim().length) {
          this.classList.add('hidden');
        }
      })
      .catch((error) => {
        console.error('Recently viewed products error:', error.message);
        this.classList.add('hidden');
      });
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
